import React, { useMemo } from 'react';
import { Text } from '@fiverr-private/typography';
import { Stack } from '@fiverr-private/layout_components';
import { Checkbox, Radio, RadioGroup } from '@fiverr-private/forms';
import { I18n } from '@fiverr-private/i18n-react';
import { FreelancerType, FreelancerTypeMinLevel } from '../../utils/types';
import {
  agencyPreferenceKeys,
  agencyPreferenceValues,
  minLevelKeys,
  minLevelTooltip,
  minLevelValues,
  T_FREELANCER_TYPE_PREFIX,
} from '../../utils/consts';
import { TooltipInfo } from '../Tooltip';
import { useIdealCandidateContext } from '../../utils/context';

const FiverrLevel = ({
  minLevel,
  setMinLevel,
}: {
  minLevel: FreelancerType['minLevel'];
  setMinLevel: (value: string) => void;
}) => {
  const currentMinLevel = useMemo(() => {
    if (minLevel === undefined) {
      return undefined;
    }
    return minLevel ?? 'ANY_LEVEL';
  }, [minLevel]);

  return (
    <RadioGroup name="level" value={currentMinLevel} onChange={(value) => setMinLevel(minLevelValues[value])}>
      {minLevelKeys.map((level) => (
        <Radio key={level} value={level}>
          <Stack direction="row" alignItems="center" gap="2">
            <I18n k={`${T_FREELANCER_TYPE_PREFIX}.${level.toLocaleLowerCase()}`} />
            {level !== 'ANY_LEVEL' && (
              <TooltipInfo contentCopy="type_level.tooltip.level" params={{ params: minLevelTooltip[level] }} />
            )}
          </Stack>
        </Radio>
      ))}
    </RadioGroup>
  );
};

const VettedPro = ({
  isVettedPro,
  setIsVettedPro,
}: {
  isVettedPro: FreelancerType['isVettedPro'];
  setIsVettedPro: (value: boolean) => void;
}) => (
  <Stack direction="column" gap="4" fontSize="b_md">
    <Stack direction="row" alignItems="center" display="flex" gap="2">
      <Text fontWeight="semibold">
        <I18n k={`${T_FREELANCER_TYPE_PREFIX}.type.vetted_pro`} />
      </Text>
      <TooltipInfo contentCopy="type_level.tooltip.vetted_pro" />
    </Stack>
    <Checkbox checked={!!isVettedPro} onChange={(e) => setIsVettedPro(e.target.checked)}>
      <I18n k={`${T_FREELANCER_TYPE_PREFIX}.prioritize_vetted_pro`} />
    </Checkbox>
  </Stack>
);

const Agencies = ({
  agencyPreference,
  setAgencyPreference,
}: {
  agencyPreference: FreelancerType['agencyPreference'];
  setAgencyPreference: (value: string) => void;
}) => {
  const currentAgency = useMemo(() => {
    if (agencyPreference === undefined) {
      return undefined;
    }
    return agencyPreference ?? 'NO_PREFERENCE';
  }, [agencyPreference]);

  return (
    <Stack direction="column" gap="4">
      <Stack direction="row" alignItems="center" display="flex" gap="2">
        <Text fontWeight="semibold">
          <I18n k={`${T_FREELANCER_TYPE_PREFIX}.type.agencies`} />
        </Text>
        <TooltipInfo contentCopy="type_level.tooltip.agencies" />
      </Stack>
      <RadioGroup
        name="agencies"
        value={currentAgency}
        onChange={(value) => setAgencyPreference(agencyPreferenceValues[value])}
      >
        {agencyPreferenceKeys.map((key) => (
          <Radio key={key} value={key}>
            <I18n k={`${T_FREELANCER_TYPE_PREFIX}.${key.toLocaleLowerCase()}`} />
          </Radio>
        ))}
      </RadioGroup>
    </Stack>
  );
};

export const TypeAndLevel = () => {
  const { freelancerType, setFreelancerType } = useIdealCandidateContext();
  return (
    <Stack direction="column" gap="10">
      <FiverrLevel
        minLevel={freelancerType?.minLevel}
        setMinLevel={(minLevel) =>
          setFreelancerType({ ...freelancerType, minLevel: minLevel as FreelancerTypeMinLevel })
        }
      />
      <VettedPro
        isVettedPro={freelancerType?.isVettedPro}
        setIsVettedPro={(isVettedPro) => {
          const { agencyPreference, minLevel } = freelancerType || {};
          setFreelancerType(
            isVettedPro ? { ...{ agencyPreference, minLevel }, isVettedPro } : { ...{ agencyPreference, minLevel } }
          );
        }}
      />
      <Agencies
        agencyPreference={freelancerType?.agencyPreference}
        setAgencyPreference={(agencyPreference) =>
          setFreelancerType({
            ...freelancerType,
            agencyPreference: agencyPreference as FreelancerType['agencyPreference'],
          })
        }
      />
    </Stack>
  );
};
